import React, { useEffect, useRef } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Checkbox } from './Checkbox';
import { ChampEtiquete } from './ChampEtiquete';
import { EnteteSection } from './EnteteSection';
import { MonthYearField } from './MonthYearField';
import { X } from './icons';
import { formatMonthYear } from '../utils/monthYear';
import { QuantifyHint } from './QuantifyHint';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';
import type { MonthYear, ProjectEntry } from '../types';
import { ongoingLabel } from '../utils/documentLabels';
import type { OutputLanguage } from '../types';
import { t } from '../i18n';

function emptyEntry(): ProjectEntry {
  return { name: '', date: { month: '', year: '' } as MonthYear, current: false, description: '' };
}

function dateLabel(date: MonthYear, current: boolean, ongoing: string): string {
  const d = formatMonthYear(date);
  if (current) return d ? `${d} – ${ongoing}` : ongoing;
  return d;
}

export function serializeProjects(
  entries: ProjectEntry[],
  // Langue du DOCUMENT : « En cours » est une valeur écrite dans le CV, pas un libellé.
  outputLanguage: OutputLanguage = 'fr',
): string {
  const ongoing = ongoingLabel(outputLanguage);
  return entries
    .map((p) => {
      const name = p.name.trim();
      const desc = p.description.trim();
      if (!name && !desc) return '';
      const label = dateLabel(p.date, p.current, ongoing);
      const head = label ? `${name} (${label})` : name;
      return desc ? `${head} : ${desc}` : head;
    })
    .filter(Boolean)
    .join('\n');
}

interface ProjectEditorProps {
  value: ProjectEntry[];
  onChange: (entries: ProjectEntry[]) => void;
  testID?: string;
}

export function ProjectEditor({ value, onChange, testID }: ProjectEditorProps) {
  const update = (index: number, patch: Partial<ProjectEntry>) =>
    onChange(value.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));
  // Rang de la ligne qui vient d'être ajoutée : son premier champ prend le focus à l'apparition,
  // sans quoi une ligne créée sous le bas de l'écran passerait inaperçue.
  const nouvelle = useRef(-1);
  // Une seule fois : sans cela, une ligne remontée plus tard à ce rang reprendrait le focus.
  useEffect(() => {
    nouvelle.current = -1;
  });

  return (
    <View testID={testID} style={styles.container}>
      <EnteteSection
        titre={t('editors.project.titre')}
        ajouterA11y={t('editors.project.add')}
        ajouterTestID="proj-add"
        onAjouter={() => {
          nouvelle.current = value.length;
          onChange([...value, emptyEntry()]);
        }}
      />

      {value.map((entry, index) => (
        <View key={index} testID={`proj-row-${index}`} style={styles.row}>
          <View style={styles.rowHeader}>
            <Text style={styles.rowTitle}>{t('editors.project.rowTitle', { n: index + 1 })}</Text>
            <Pressable
              testID={`proj-remove-${index}`}
              accessibilityRole="button"
              accessibilityLabel={t('a11y.removeProject')}
              onPress={() => remove(index)}
              style={styles.remove}
            >
              <X size={18} color={colors.grey500} />
            </Pressable>
          </View>

          <ChampEtiquete
            testID={`proj-name-${index}`}
            autoFocus={index === nouvelle.current}
            label={t('editors.project.name')}
            value={entry.name}
            onChangeText={(name) => update(index, { name })}
          />

          <Text style={styles.miniLabel}>{t('editors.project.date')}</Text>
          <MonthYearField
            value={entry.date}
            onChange={(date) => update(index, { date })}
            testID={`proj-date-${index}`}
          />
          <Checkbox
            label={t('editors.project.current')}
            checked={entry.current}
            onToggle={(current) => update(index, { current })}
            testID={`proj-current-${index}`}
          />

          <ChampEtiquete
            testID={`proj-desc-${index}`}
            label={t('editors.project.descriptionLabel')}
            placeholder={t('editors.project.descriptionPlaceholder')}
            multiline
            value={entry.description}
            onChangeText={(description) => update(index, { description })}
          />
        </View>
      ))}

      {value.length > 0 ? <QuantifyHint /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  row: {
    gap: spacing.sm,
    backgroundColor: colors.white,
    borderRadius: formes.rayon.md,
    padding: spacing.md,
    ...shadow.card,
  },
  rowHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rowTitle: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  remove: { minWidth: formes.cible, minHeight: formes.cible, alignItems: 'flex-end', justifyContent: 'center' },
  miniLabel: {
    fontSize: typography.caption.size,
    fontWeight: '700',
    color: colors.grey700,
  },
});
